import { AuthState } from "../base/AuthState";

import template from './PasswordResetTemplate.html';
import { _lcs } from "../../i18n";
import { validateScope, bindLiveClear } from "../../components/general/form-validation";

/**
 * PasswordResetState — choose a new password (#/password-reset/{token}).
 *
 * Reached from TokenState for a resetPassword token. Redeeming the token
 * (PUT /auth/token/{token} with the new password) sets the password and signs
 * the account out everywhere, so the operator then logs in afresh.
 *
 * The token is looked up again on entry — the route can be opened directly
 * too — and anything that is not a live resetPassword token goes back to
 * TokenState, which owns the "link invalid / expired" messages.
 */
export class PasswordResetState extends AuthState
{
    static ROUTE = '/password-reset/:token';
    static ID = 'password-reset';

    // Same rule as the core-api (TokenPutRequest) and the settings screen.
    static MIN_PASSWORD = 6;

    async enter()
    {
        this.saving = false;
        this.tokenHash = this.getParam( 'token' );

        const token = await this.loadToken();
        if ( !token )
        {
            this.app.router.redirect( "/token/" + encodeURIComponent( this.tokenHash || "" ) );
            return;
        }

        const host = this.app.container.querySelector( "#main-wrapper" ) || document.body;
        this.app.view.render( host, template, {
            "email" : token.actionData?.user?.email || "",
            "minLength" : PasswordResetState.MIN_PASSWORD
        } );
        this.host = host;

        this.form = host.querySelector( '[data-id="reset-form"]' );
        this.errorEl = host.querySelector( '[data-id="reset-error"]' );
        bindLiveClear( this.form );

        this.addEventListener( this.form, "submit", ( e ) => this.onSubmit( e ) );
        this.addEventListener( this.form, "click", ( e ) => this.onToggle( e ) );

        const toLogin = ( e ) =>
        {
            e.preventDefault();
            this.app.router.redirect( "/login" );
        };
        this.addEventListener( host.querySelector( '[data-id="btn-back-to-login"]' ), "click", toLogin );
        this.addEventListener( host.querySelector( '[data-id="btn-to-login"]' ), "click", toLogin );

        host.querySelector( "#reset-new" )?.focus();
    }

    /** The token, if it is a live resetPassword token — else null. */
    async loadToken()
    {
        if ( !this.tokenHash )
        {
            return null;
        }
        try
        {
            const { result } = await this.app.api.get( '/auth/token/' + encodeURIComponent( this.tokenHash ) );
            const token = ( result && +result.status === 200 ) ? result.data : null;
            return ( token && token.action === "resetPassword" ) ? token : null;
        }
        catch ( err )
        {
            return null;
        }
    }

    /** Show / hide one password field (the eye button inside it). */
    onToggle( e )
    {
        const toggle = e.target.closest( "[data-toggle]" );
        if ( !toggle )
        {
            return;
        }
        const input = this.form.querySelector( "#" + toggle.getAttribute( "data-toggle" ) );
        const show = input.type === "password";
        input.type = show ? "text" : "password";
        toggle.setAttribute( "aria-pressed", String( show ) );
        toggle.setAttribute( "aria-label", show ? _lcs( "Passwort verbergen" ) : _lcs( "Passwort anzeigen" ) );
        const icon = toggle.querySelector( ".icon" );
        icon.classList.toggle( "icon--eye", !show );
        icon.classList.toggle( "icon--eye-off", show );
        input.focus();
    }

    async onSubmit( e )
    {
        e.preventDefault();
        if ( this.saving )
        {
            return;
        }

        const next = this.form.querySelector( "#reset-new" );
        const repeat = this.form.querySelector( "#reset-repeat" );
        repeat.setCustomValidity( repeat.value && repeat.value !== next.value
            ? _lcs( "Die Passwörter stimmen nicht überein." ) : "" );

        if ( !validateScope( this.form ) )
        {
            return;
        }

        this.errorEl.hidden = true;
        this.setSaving( true );
        try
        {
            const { result } = await this.app.api.put(
                '/auth/token/' + encodeURIComponent( this.tokenHash ),
                { password : next.value }
            );
            if ( !result || +result.status !== 200 )
            {
                throw new Error( "Unexpected token response." );
            }
            this.showDone();
        }
        catch ( err )
        {
            console.error( err );
            this.setSaving( false );
            this.errorEl.textContent = this._errorMessageForStatus( +err?.status );
            this.errorEl.hidden = false;
        }
    }

    _errorMessageForStatus( status )
    {
        switch ( status )
        {
            case 400: return _lcs( "Das Passwort muss mindestens 6 Zeichen lang sein." );
            case 404: return _lcs( "Dieser Link wurde bereits verwendet oder ist nicht mehr gültig." );
            case 410: return _lcs( "Dieser Link ist abgelaufen. Bitte fordere einen neuen an." );
            default:  return _lcs( "Das Passwort konnte nicht geändert werden. Bitte versuche es später erneut." );
        }
    }

    showDone()
    {
        this.host.querySelector( '[data-id="reset-request"]' ).hidden = true;
        this.host.querySelector( '[data-id="btn-back-to-login"]' ).hidden = true;
        this.host.querySelector( '[data-id="reset-done"]' ).hidden = false;
    }

    setSaving( on )
    {
        this.saving = on;
        const btn = this.form.querySelector( 'button[type="submit"]' );
        btn.disabled = on;
        btn.setAttribute( "aria-busy", String( on ) );
    }

    async exit()
    {
    }
}
